import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@shared/ui/dialog";
import React, { useEffect } from "react";
import {
  useCreateEmailSignature,
  useGetEmailSignatures,
  useUpdateEmailSignature,
} from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { EmailSignatureData } from "../../../types";

const Emailcreate = ({
  openCreateEmail,
  setOpenCreateEmail,
  selectedSignature,
}: {
  openCreateEmail: boolean;
  setOpenCreateEmail: (value: boolean) => void;
  selectedSignature: any | null;
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const createEmailSignature = useCreateEmailSignature();
  const updateEmailSignature = useUpdateEmailSignature(selectedSignature?.id);
  const isEditMode = Boolean(selectedSignature);
  const { data: emailSignature } = useGetEmailSignatures(
    selectedSignature?.id,
    openCreateEmail
  );

  const formFields: FormField[] = [
    {
      name: "signatureName",
      type: "text",
      label: "Signature Name",
      placeholder: "e.g., Professional Casual, Support Team",
      required: true,
      className: "col-span-2",
    },
    {
      name: "content",
      type: "rich-text",
      label: "Content",
      rows: 10,
      required: false,
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      signatureName: "",
      content: "",
    },
  });

  // Populate form when dialog opens or selectedSignature changes
  useEffect(() => {
    if (openCreateEmail && selectedSignature && emailSignature?.result) {
      // Only populate when editing AND data is loaded
      const initialValues = {
        signatureName: emailSignature.result.name || "",
        content: emailSignature.result.content || "",
      };
      FormService.populateForm(form, initialValues, formFields);
    } else if (openCreateEmail && !selectedSignature) {
      // Reset form for create mode
      form.reset({
        signatureName: "",
        content: "",
      });
    }
  }, [openCreateEmail, selectedSignature, emailSignature]);

  const handleCancel = () => {
    form.reset({
      signatureName: "",
      content: "",
    });
    setOpenCreateEmail(false);
  };

  const handleSubmit = async () => {
    try {
      const values = form.getValues();
      const operation = isEditMode ? "update" : "create";

      switch (operation) {
        case "update":
          const updatePayload = {
            remote_utility_id: remoteUtilityId,
            name: values.signatureName,
            content: values.content,
          };
          await updateEmailSignature.mutateAsync(updatePayload);
          break;

        case "create":
          const createPayload = {
            remote_utility_id: remoteUtilityId,
            name: values.signatureName,
            content: values.content,
            is_default: false,
          };
          await createEmailSignature.mutateAsync(createPayload);
          break;
      }

      handleCancel();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return (
    <div>
      <Dialog open={openCreateEmail} onOpenChange={setOpenCreateEmail}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {isEditMode ? "Edit Signature" : "Create New Signature"}
            </DialogTitle>
            <DialogDescription>
              {isEditMode
                ? "Update your email signature details"
                : "Create a new email signature to use in your templates."}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <DynamicForm
              fields={formFields}
              form={form}
              gridColumns={2}
              responsiveColumns={{ sm: 1, md: 2 }}
              onSubmit={handleSubmit}
              onReset={handleCancel}
              config={{
                resetButtonText: "Cancel",
                submitButtonText: isEditMode
                  ? "Update Signature"
                  : "Save Signature",
                layout: "grid",
                columns: 2,
                showSubmitButton: false,
                showResetButton: false,
                spacing: "medium",
                classes: {
                  container: "w-full mb-4",
                  label: "block text-sm font-medium text-gray-700",
                  buttonContainer: "flex justify-end gap-2",
                  input:
                    "w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                  select:
                    "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none",
                  error: "text-red-600 text-sm mt-1",
                  helperText: "text-xs text-gray-500 mt-1",
                },
              }}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {isEditMode ? "Update Signature" : "Create Signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Emailcreate;
